import { Fragment } from 'react';
import { GameplayContext } from '../../data/controls.config';
import { ActionForContext, getKeyLabel } from '../../utils/controls.utils';
import { useControlsStore } from '../../store/controlsSlice';
import { useInputSourceStore } from '../../store/inputSourceSlice';
import { useKeyboardLayoutStore } from '../../store/keyboardLayoutSlice';
import {
  ActionMapping,
  GamepadBinding,
  KeyboardBinding,
} from '../../types/controls.types';
import {
  getGamepadAxisSprite,
  getGamepadButtonSprite,
  getKeyboardCodeSprite,
  getKeySpriteUrls,
} from '../../data/keys.config';
import './KeyDisplay.scss';

interface Part {
  // `null` = no sprite mapping → fall back to rendering `label` as a text chip.
  spriteUrls: string[] | null;
  label: string;
}

function partFromKeyboardCode(code: string): Part {
  const id = getKeyboardCodeSprite(code);
  return {
    spriteUrls: id ? getKeySpriteUrls(id) : null,
    label: getKeyLabel(code),
  };
}

function partFromGamepadButton(index: number, label: string): Part {
  const id = getGamepadButtonSprite(index);
  return {
    spriteUrls: id ? getKeySpriteUrls(id) : null,
    label,
  };
}

function decomposeKeyboard(binding: KeyboardBinding): Part[] {
  const parts: Part[] = [];
  for (const m of binding.modifiers ?? []) {
    parts.push(partFromKeyboardCode(m));
  }
  parts.push(partFromKeyboardCode(binding.code));
  return parts;
}

function decomposeGamepad(binding: GamepadBinding): Part[] {
  switch (binding.type) {
    case 'button':
      return [partFromGamepadButton(binding.index, binding.label)];
    case 'axis': {
      const id = getGamepadAxisSprite(binding.axisIndex, binding.direction);
      return [
        {
          spriteUrls: id ? getKeySpriteUrls(id) : null,
          label: binding.label,
        },
      ];
    }
    case 'combo':
      return [
        partFromGamepadButton(binding.modifier, binding.modifierLabel),
        partFromGamepadButton(binding.button, binding.buttonLabel),
      ];
  }
}

interface Props<C extends GameplayContext> {
  context: C;
  action: ActionForContext<C>;
  className?: string;
  size?: 'sm' | 'md';
  // Force keyboard or gamepad display regardless of the last device used —
  // needed for the fixed columns of the Options > Controls screen.
  forceSource?: 'keyboard' | 'gamepad';
}

const SIZE_PX: Record<'sm' | 'md', number> = { sm: 24, md: 36 };

// see docs/controls.md — auto-switches between keyboard and gamepad based on
// `useInputSourceStore.lastSource`.
export const KeyDisplay = <C extends GameplayContext>({
  context,
  action,
  className,
  size = 'md',
  forceSource,
}: Props<C>) => {
  // Subscribe so the chip re-renders when the layout map becomes available / changes.
  useKeyboardLayoutStore((s) => s.version);

  const lastSource = useInputSourceStore((s) => s.lastSource);
  const mapping = useControlsStore((s) => {
    const ctx = context === 'interface' ? s.interface : s.dungeon;
    return (ctx as Record<string, ActionMapping>)[action as string];
  });
  if (!mapping) {
    return null;
  }

  const source = forceSource ?? lastSource;
  const parts =
    source === 'gamepad'
      ? decomposeGamepad(mapping.gamepad)
      : decomposeKeyboard(mapping.keyboard);

  const px = SIZE_PX[size];

  return (
    <span
      className={['key-display', `key-display--${size}`, className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="key-display-plus">+</span>}
          {p.spriteUrls ? (
            <span className="key-display-sprite-group">
              {p.spriteUrls.map((url, j) => (
                <img
                  key={j}
                  src={url}
                  alt={j === 0 ? p.label : ''}
                  width={px}
                  height={px}
                  className="key-display-sprite"
                  draggable={false}
                />
              ))}
            </span>
          ) : (
            <span className="key-display-text">{p.label}</span>
          )}
        </Fragment>
      ))}
    </span>
  );
};
